import { Controller } from "@hotwired/stimulus";
import { animate } from "animejs";
import { Modal } from 'flowbite';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
].join(', ');

export default class extends Controller {
    static values = {
        "count": String,
        "name": String,
        "showOnInit": Boolean
    }

    connect() {
        this.previouslyFocusedElement = null;
        this.disabledBackgroundElements = [];
        this.handleTrapFocusBound = this.handleTrapFocus.bind(this);
        this.closeButtonListeners = [];
        this.initializeModal();
    }

    disconnect() {
        this.teardownModal();
    }

    initializeModal() {
        if (this.modal) {
            return;
        }

        this.modalTarget = document.getElementById(`${this.nameValue}-modal-${this.countValue}`);
        this.modalContainerTarget = document.getElementById(`${this.nameValue}-modal-container-${this.countValue}`);
        this.modalhideTargets = document.querySelectorAll(`.${this.nameValue}-modal-hide-${this.countValue}`);

        if (!this.modalTarget || !this.modalContainerTarget) {
            return;
        }

        this.modalhideTargets.forEach(element => {
            const clickHandler = () => this.hide();
            const keydownHandler = (event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    this.hide();
                }
            };
            element.addEventListener("click", clickHandler);
            element.addEventListener("keydown", keydownHandler);
            this.closeButtonListeners.push({ element, clickHandler, keydownHandler });
        });

        const options = {
            backdrop: false,
            onHide: () => {
                this.modalTarget.style.opacity = 0;
                this.modalContainerTarget.style.transform = null;
                this.disableFocusTrap();
                this.enableBackgroundFocus();
                if (this.previouslyFocusedElement?.focus) {
                    this.previouslyFocusedElement.focus();
                }
                this.previouslyFocusedElement = null;
            },
            onShow: () => {
                animate(this.modalTarget, {
                    opacity: 1,
                    duration: 200
                });
                animate(this.modalContainerTarget, {
                    y: "-2rem",
                    duration: 200
                });

                this.disableBackgroundFocus();
                this.enableFocusTrap();
                this.focusInitialElement();
            }
        };
        this.modal = new Modal(this.modalTarget, options);

        if (this.showOnInitValue) {
            this.modal.show();
        }
    }

    teardownModal() {
        this.disableFocusTrap();
        this.enableBackgroundFocus();

        this.closeButtonListeners?.forEach(({ element, clickHandler, keydownHandler }) => {
            element.removeEventListener("click", clickHandler);
            element.removeEventListener("keydown", keydownHandler);
        });
        this.closeButtonListeners = [];

        if (this.modal) {
            this.modal.hide();
            if (typeof this.modal.destroyAndRemoveInstance === 'function') {
                this.modal.destroyAndRemoveInstance();
            }
            this.modal = null;
        }
    }

    hide() {
        if (this.modal) {
            this.modal.hide();
        }
    }

    click() {
        if (!this.modal) {
            this.initializeModal();
        }
        if (!this.modal) {
            return;
        }

        this.previouslyFocusedElement = document.activeElement;
        this.modal.show();
    }

    handleKeydown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.click();
        }
    }

    focusInitialElement() {
        if (this.nameValue === "requestInformation" || this.nameValue === "request-information") {
            const modalTitle = this.modalTarget.querySelector('h2');
            if (modalTitle) {
                modalTitle.setAttribute('tabindex', '-1');
                modalTitle.focus();
            }
            return;
        }

        const firstInput = this.modalTarget.querySelector('input:not([type="hidden"])');
        if (firstInput) {
            firstInput.focus();
        }
    }

    disableBackgroundFocus() {
        this.disabledBackgroundElements = [];

        document.querySelectorAll(FOCUSABLE_SELECTOR).forEach((element) => {
            if (this.modalTarget.contains(element)) {
                return;
            }

            this.disabledBackgroundElements.push({
                element,
                tabIndex: element.getAttribute('tabindex'),
            });
            element.setAttribute('tabindex', '-1');
        });
    }

    enableBackgroundFocus() {
        this.disabledBackgroundElements.forEach(({ element, tabIndex }) => {
            if (tabIndex === null) {
                element.removeAttribute('tabindex');
            } else {
                element.setAttribute('tabindex', tabIndex);
            }
        });
        this.disabledBackgroundElements = [];
    }

    getFocusableElements() {
        return Array.from(this.modalTarget.querySelectorAll(FOCUSABLE_SELECTOR));
    }

    enableFocusTrap() {
        document.addEventListener('keydown', this.handleTrapFocusBound, true);
    }

    disableFocusTrap() {
        document.removeEventListener('keydown', this.handleTrapFocusBound, true);
    }

    handleTrapFocus(event) {
        if (event.key !== 'Tab' || !this.isModalOpen()) {
            return;
        }

        const focusableElements = this.getFocusableElements();
        if (focusableElements.length === 0) {
            event.preventDefault();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;
        const focusIsInsideModal = this.modalTarget.contains(activeElement);

        if (event.shiftKey) {
            if (!focusIsInsideModal || activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            }
            return;
        }

        if (!focusIsInsideModal || activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    }

    isModalOpen() {
        if (!this.modalTarget) {
            return false;
        }

        if (typeof this.modal?.isVisible === 'function') {
            return this.modal.isVisible();
        }

        return !this.modalTarget.classList.contains('hidden');
    }
}
